import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';

// ============================================================================
// External Declarations
// ============================================================================

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getSubmoduleOrKerktonRoleFromLogEntry = _wg('getSubmoduleOrKerktonRoleFromLogEntry');
const generalLogEntryTemplate = _wg('generalLogEntryTemplate');

// ============================================================================
// Module-level State
// ============================================================================

let logRefreshInterval: ReturnType<typeof setInterval> | null = null;
let refreshCallback: (() => void) | null = null;

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 50;

// ============================================================================
// Grid Columns
// ============================================================================

function getGridColumns(): KstDataGridColumn[] {
  return [
    {
      field: 'dateCreated',
      name: 'Date/Time',
      size: 'col-xs-2',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.dateCreated)) {
          return moment(e.dateCreated).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT);
        }
        return '';
      },
    },
    {
      field: 'creatorFirstName',
      name: 'First',
      size: 'col-xs-1',
    },
    {
      field: 'creatorLastName',
      name: 'Last',
      size: 'col-xs-1',
    },
    {
      field: 'role',
      name: 'Role',
      size: 'col-xs-1',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.role)) {
          return getSubmoduleOrKerktonRoleFromLogEntry(e);
        }
        return '';
      },
    },
    {
      field: 'action',
      name: 'Action',
      size: 'col-xs-2',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.action)) {
          return e.action;
        }
        return '';
      },
    },
    {
      field: 'entry',
      name: 'Entry',
      size: 'col-xs-5',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.entry)) {
          return generalLogEntryTemplate(e.entry);
        }
        return '';
      },
    },
  ];
}

// ============================================================================
// LogScreen Component
// ============================================================================

export function LogScreen() {
  const { theme } = useTheme();

  // --- State ---
  const [newEntry, setNewEntry] = useState('');
  const [gridRows, setGridRows] = useState<KstDataGridRows>({ entries: null });
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [gridPager, setGridPager] = useState<KstDataGridPager>({
    curPage: 1,
    numPages: 1,
    records: 0,
    isVisible: false,
  });

  // --- Refs ---
  const currentPageRef = useRef(1);
  const isFirstLoadRef = useRef(true);
  const newEntryInputRef = useRef<HTMLInputElement>(null);

  // --- Column Definitions ---
  const gridColumns = useMemo(() => getGridColumns(), []);

  // --- Load Log Entries ---
  const loadLogEntries = useCallback((pageOverride?: number) => {
    setIsLogLoading(true);
    const page = pageOverride !== undefined ? pageOverride : currentPageRef.current;
    const authTokenRefresh = isFirstLoadRef.current;

    const operation = {
      data: {
        page,
        take: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
      },
    };

    api.getRadioLogEntries(page, PAGE_SIZE.toString(), JSON.stringify(operation), authTokenRefresh, (result: any) => {
      const totalRecords = result.records || 0;
      const logs = result.logs || [];

      const rows = logs.map((log: any) => ({
        id: log.id,
        dateCreated: log.dateCreated,
        creatorFirstName: log.creatorFirstName,
        creatorLastName: log.creatorLastName,
        role: log.role,
        action: log.action,
        entry: log.entry,
      }));

      setGridRows({ entries: rows });
      setGridPager({
        curPage: page,
        numPages: Math.ceil(totalRecords / PAGE_SIZE),
        records: totalRecords,
        isVisible: totalRecords > PAGE_SIZE,
      });

      isFirstLoadRef.current = false;
      setIsLogLoading(false);
    }, () => {
      App.showPopupNotification('Error getting log entries.', 'error');
      setIsLogLoading(false);
    });
  }, []);

  // --- Pagination ---
  const goToPage = useCallback((page: number) => {
    currentPageRef.current = page;
    loadLogEntries(page);
  }, [loadLogEntries]);

  // --- Refresh ---
  const refreshGrid = useCallback(() => {
    loadLogEntries();
  }, [loadLogEntries]);

  // --- Add New Entry ---
  const addNewEntry = useCallback(() => {
    if (!newEntry.trim()) return;

    api.addRadioLogEntry(newEntry, () => {
      setNewEntry('');
      currentPageRef.current = 1;
      loadLogEntries(1);

      // Focus back on input
      if (newEntryInputRef.current) {
        newEntryInputRef.current.focus();
      }
    });
  }, [newEntry, loadLogEntries]);

  // --- Handle Enter Key ---
  const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      addNewEntry();
    }
  }, [addNewEntry]);

  // Register refresh callback
  useEffect(() => {
    refreshCallback = refreshGrid;
    return () => {
      refreshCallback = null;
    };
  }, [refreshGrid]);

  // Start/stop page refresh interval (handles both registry and legacy mount paths)
  useEffect(() => {
    resetLogPageRefresh();
    return () => {
      clearLogPageRefresh();
    };
  }, []);

  // --- Initial Load ---
  useEffect(() => {
    loadLogEntries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Render ---
  return (
    <div id="GeneralLogSection" className={`log-screen ${theme}`.trim()}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">General Log</h3>
              </div>

              {/* New Entry Form */}
              <div className="kst-form-card" style={{ marginBottom: '16px' }}>
                <div className="kst-form-card-header">New Entry</div>
                <div className="kst-form-card-body">
                  <div className="kst-log-entry-form">
                    <input
                      type="text"
                      className="kst-log-entry-input"
                      value={newEntry}
                      onChange={(e) => setNewEntry(e.target.value)}
                      onKeyUp={handleKeyUp}
                      placeholder="Type your log entry..."
                      ref={newEntryInputRef}
                    />
                    <button
                      type="button"
                      className="kstc-btn kstc-btn--primary"
                      onClick={addNewEntry}
                      disabled={!newEntry.trim()}
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '16px', height: '16px', marginRight: '4px' }}>
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Entry
                    </button>
                  </div>
                </div>
              </div>

              {/* Log Grid */}
              <KstDataGrid
                columns={gridColumns}
                rows={gridRows}
                pager={gridPager}
                showheader={false}
                showcheckbox={false}
                mobiletitlefield="dateCreated"
                loading={isLogLoading}
                onGoToPage={goToPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Page Refresh Management
// ============================================================================

function clearLogPageRefresh(): void {
  if (logRefreshInterval) {
    clearInterval(logRefreshInterval);
    logRefreshInterval = null;
  }
}

function resetLogPageRefresh(): void {
  clearLogPageRefresh();
  logRefreshInterval = setInterval(() => {
    if (refreshCallback) {
      refreshCallback();
    }
  }, 60000);
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).clearGeneralLogRefresh = clearLogPageRefresh;
(window as any).resetLogPageRefresh = resetLogPageRefresh;

// ============================================================================
// Screen Registry
// ============================================================================

export {
  clearLogPageRefresh,
  resetLogPageRefresh,
};
